import SEO from '@/seo';
import { Link } from '@holo/link';
import { Container, H1, Text } from '@holo/ui';
import type { GetStaticProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return {
    props: await serverSideTranslations(locale ?? 'en'),
  };
};

const Page = () => {
  return (
    <Container maxWidth={700} marginTop={80} className="content" paddingHorizontal={20}>
      <SEO title="Terms of Service" />
      <H1 marginBottom={20}>Terms of Service</H1>
      <p>Last updated on: March 14, 2018</p>
      <p>
        By signing up for the Holo services or any of the services of Holo Economy Inc. or its affiliates (“Holo”) you
        are agreeing to be bound by the following terms and conditions (“Terms of Service”). The services offered by
        Holo under the Terms of Service include various products and services to help you sell goods and services to
        buyers, whether online (“Online Services”), in person (“POS Services”), or both. Any such services offered by
        Holo are referred to in these Terms of Services as the “Services”. Any new features or tools which are added to
        the current Services shall be also subject to the Terms of Service. You can review the current version of the
        Terms of Service at any time at Terms of Service. Holo reserves the right to update and change the Terms of
        Service by posting updates and changes to the Holo website. You are advised to check the Terms of Service from
        time to time for any updates or changes that may impact you.
      </p>
      <p>
        You must read, agree with and accept all of the terms and conditions contained in this Terms of Service
        agreement, including Holo’s Privacy Policy before you may become a Holo user.
      </p>
      <p>
        <strong>1. Account Terms</strong>
        <ul>
          <li>
            To access and use the Services, you must register for a Holo account (“Account”) by providing your full
            legal name, current address, phone number, a valid email address, and any other information indicated as
            required. Holo may reject your application for an Account, or cancel an existing Account, for any reason, in
            our sole discretion.
          </li>
          <li>
            You are responsible for keeping your password secure. Holo cannot and will not be liable for any loss or
            damage from your failure to maintain the security of your Account and password.
          </li>
          <li>
            A breach or violation of any term in the Terms of Service, as determined in the sole discretion of Holo will
            result in an immediate termination of your services.
          </li>
        </ul>
      </p>

      <p>
        <strong>2. Account Activation</strong> <br />
        <strong>2.1 Holo Account</strong>
        <ul>
          <li>
            Subject to section 2.1.2, the person signing up for the Service will be the contracting party (“Account
            Owner”) for the purposes of our Terms of Service and will be the person who is authorized to use any
            corresponding account we may provide to the Account Owner in connection with the Service.
          </li>
          <li>
            If you are signing up for the Service on behalf of your employer, your employer shall be the Account Owner.
            If you are signing up for the Service on behalf of your employer, then you represent and warrant that you
            have the authority to bind your employer to our Terms of Service.
          </li>
        </ul>
      </p>
      <p>
        <strong>3. General Conditions</strong>
        <br />
        You must read, agree with and accept all of the terms and conditions contained in these Terms of Service,
        including the <Link href="/privacy-policy">Privacy Policy</Link> before you may become a member of Holo.
        <ul>
          <li>Technical support is only provided to paying Account holders and is only available via email.</li>
          <li>
            The Terms of Service shall be governed by and interpreted in accordance with the laws of the of the State of
            New York and the parties submit to the jurisdiction of the courts of New York.
          </li>
          <li>
            You acknowledge and agree that Holo may amend these Terms of Service at any time by posting the relevant
            amended and restated Terms of Service on Holo’s website, available at Terms of Service and such amendments
            to the Terms of Service are effective as of the date of posting. Your continued use of the Services after
            the amended Terms of Service are posted to Holo’s website constitutes your agreement to, and acceptance of,
            the amended Terms of Service. If you do not agree to any changes to the Terms of Service, do not continue to
            use the Service.
          </li>
          <li>
            You may not use the Holo service for any illegal or unauthorized purpose nor may you, in the use of the
            Service, violate any laws in your jurisdiction (including but not limited to copyright laws), the laws
            applicable to you in your customer’s jurisdiction, or the laws of United States and the State of New York.
            You will comply with all applicable laws, rules and regulations in your use of the Service.
          </li>
          <li>
            You agree not to reproduce, duplicate, copy, sell, resell or exploit any portion of the Service, use of the
            Service, or access to the Service without the express written permission by Holo.
          </li>
          <li>
            You understand that your Materials (not including credit card information), may be transferred unencrypted
            and involve (a) transmissions over various networks; and (b) changes to conform and adapt to technical
            requirements of connecting networks or devices. Credit Card information is always encrypted during transfer
            over networks.
          </li>
          <li>
            You acknowledge and agree that your use of the Service, including information transmitted to or stored by
            Holo, is governed by its privacy policy at <Link href="/privacy-policy">Privacy Policy</Link>
          </li>
        </ul>
      </p>
      <p>
        <strong>4. Holo Rights</strong>
        <br />
        <ul>
          <li>We reserve the right to modify or terminate the Service for any reason, without notice at any time.</li>
          <li>We reserve the right to refuse service to anyone for any reason at any time.</li>
        </ul>
      </p>
      <p>
        <strong>5. Limitation of Liability</strong>
        <br />
        <ul>
          <li>
            You expressly understand and agree that Holo shall not be liable for any direct, indirect, incidental,
            special, consequential or exemplary damages, including but not limited to, damages for loss of profits,
            goodwill, use, data or other intangible losses resulting from the use of or inability to use the service.
          </li>
          <li>
            In no event shall Holo or our suppliers be liable for lost profits or any special, incidental or
            consequential damages arising out of or in connection with our site, our services or these Terms of Service
            (however arising including negligence). You agree to indemnify and hold us and (as applicable) our parent,
            subsidiaries, affiliates, Holo partners, officers, directors, agents, employees, and suppliers harmless from
            any claim or demand, including reasonable attorneys’ fees, made by any third party due to or arising out of
            your breach of these Terms of Service, or your violation of any law or the rights of a third party.
          </li>
          <li>
            Your use of the Service is at your sole risk. The Service is provided on an “as is” and “as available” basis
            without any warranty or condition, express, implied or statutory.
          </li>
          <li>Holo does not warrant that the Service will be uninterrupted, timely, secure, or error-free.</li>
          <li>
            Holo does not warrant that the results that may be obtained from the use of the Service will be accurate or
            reliable.
          </li>
          <li>
            Holo does not warrant that the quality of any products, services, information, or other Materials purchased
            or obtained by you through the Service will meet your expectations, or that any errors in the Service will
            be corrected.
          </li>
        </ul>
      </p>
      <p>
        <strong>6. Waiver and Complete Agreement</strong>
        <br />
        The failure of Holo to exercise or enforce any right or provision of the Terms of Service shall not constitute a
        waiver of such right or provision. The Terms of Service and the documents it incorporates by reference
        constitute the entire agreement between you and Holo and govern your use of the Service, superseding any prior
        agreements between you and Holo (including, but not limited to, any prior versions of the Terms of Service).
      </p>
      <p>
        <strong>7. Intellectual Property and Customer Content</strong>
        <br />
        <ul>
          <li>
            We do not claim any intellectual property rights over the Materials you provide to the Holo service. All
            Materials you upload remains yours. You can remove your Holo store at any time by deleting your Account.
          </li>
          <br />
          <li>
            By uploading Materials, you agree: (a) to allow other internet users to view the Materials you post publicly
            to your store; (b) to allow Holo to store, and in the case of Materials you post publicly, display, your
            Materials; and (c) that Holo can, at any time, review all the Materials submitted to its Service, although
            Holo is not obligated to do so.
          </li>
          <li>
            You retain ownership over all Materials that you upload to a Holo store; however, by making your store
            public, you agree to allow others to view Materials that you post publicly to your store. You are
            responsible for compliance of the Materials with any applicable laws or regulations. We will not disclose
            your confidential information to third parties, except as required in the course of providing our services.
            Confidential information includes any Materials or information provided by you to us which is not publicly
            known. Confidential information does not include information that: (a) was in the public domain at the time
            we received it; (b) comes into the public domain after we received it through no fault of ours; (c) we
            received from someone other than you without breach of our or their confidentiality obligations; or (d) we
            are required by law to disclose.
          </li>
          <li>
            Holo shall have the non-exclusive right and license to use the names, trademarks, service marks and logos
            associated with your store to promote the Service.
          </li>
        </ul>
      </p>
      <p>
        <strong>8. Cancellation and Termination</strong>
        <br />
        <ul>
          <li>You may cancel your Account at anytime.</li>
          <li>Upon termination of the Services by either party for any reason:</li>
          <li>
            Holo will cease providing you with the Services and you will no longer be able to access your Account; any
            outstanding balance owed to Holo for your use of the Services through the effective date of such termination
            will immediately become due and payable in full and your store website will be taken offline.
          </li>
          <li>
            If at the date of termination of the Service, there are any outstanding Fees owing by you, you will receive
            one final invoice via email. Once that invoice has been paid in full, you will not be charged again.
          </li>
          <li>
            We reserve the right to modify or terminate the Holo Service or your Account for any reason, without notice
            at any time.
          </li>
          <li>
            Fraud: Without limiting any other remedies, Holo may suspend or terminate your Account if we suspect that
            you (by conviction, settlement, insurance or escrow investigation, or otherwise) have engaged in fraudulent
            activity in connection with the Site.
          </li>
        </ul>
      </p>
      <p>
        <strong>9. Modifications to the Service and Prices</strong>
        <br />
        <li>
          Prices for using the Services are subject to change upon 30 days notice from Holo. Such notice may be provided
          at any time by posting the changes to the Holo Site (Holo.ag) or the administration menu of your Holo store
          via an announcement.
        </li>
        <li>
          Holo reserves the right at any time, and from time to time, to modify or discontinue, the Service (or any part
          thereof) with or without notice.
        </li>
        <li>
          Holo shall not be liable to you or to any third party for any modification, price change, suspension or
          discontinuance of the Service.
        </li>
      </p>
      <p>
        <strong>10. Privacy & Data Protection</strong>
        <br />
        Holo is firmly committed to protecting the privacy of your personal information and the personal information of
        your customers. By using the Service, you acknowledge and agree that Holo’s collection, usage and disclosure of
        this personal information is governed by our <Link href="/privacy-policy">Privacy Policy</Link>.
      </p>

      <Text marginTop={20} fontStyle="italic">
        Fellow Farmer Inc. <br />
        15 William St 20c <br />
        New York, NY 1005 U.S.A.
      </Text>
    </Container>
  );
};

export default Page;
